import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AdminLayout } from '@/components/AdminLayout';
import { productApi, categoryApi } from '@/lib/api';
import { colorApi } from '@/lib/api';
import { sizeApi } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizesfromCollection, setSizesFromCollection] = useState([]);
  const [colorsfromCollection, setColorsFromCollection] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    images: ['https://megamart.co.ke/bica/'],
    stock: '100',
    featured: false,
    sizes: [],
    colors: [],
  });

  useEffect(() => {
    loadColors();
  }, []);

  const loadColors = async () => {
    try {
      const res = await colorApi.getAll(); // <-- backend endpoint for colors
      setColors(res.data);
    } catch (error) {
      toast.error('Failed to load colors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSizes();
  }, []);

  const loadSizes = async () => {
    try {
      const res = await sizeApi.getAll(); // <-- backend endpoint for sizes
      setSizes(res.data);
    } catch (error) {
      toast.error('Failed to load sizes');
    } finally {
      setLoading(false);
    }
  };
  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productApi.getAll({ limit: 200 }),
        categoryApi.getAll(),
      ]);

      setProducts(productsRes.data);
      setCategories(categoriesRes.data);

    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      category_id: '',
      images: ['https://megamart.co.ke/bica/'],
      stock: '100',
      featured: false,
      sizes: [],
      colors: [],
    });
    setEditingProduct(null);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        original_price: product.original_price?.toString() || '',
        category_id: product.category_id,
        images: product.images?.length ? product.images : ['https://megamart.co.ke/bica/'],
        stock: product.stock.toString(),
        featured: product.featured,
        sizes: Array.isArray(product.sizes) ? product.sizes : [],
        colors: Array.isArray(product.colors) ? product.colors : [],
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const updateImage = (index, value) => {
    const updated = [...formData.images];
    updated[index] = value;
    setFormData({ ...formData, images: updated });
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const addImage = () => {
    if (formData.images.length < 8) {
      setFormData({ ...formData, images: [...formData.images, 'https://megamart.co.ke/bica/'] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error('Please fill in required fields');
      return;
    }

    const invalidUrls = formData.images.filter(
      (url) => url && !isValidUrl(url)
    );
    if (invalidUrls.length) {
      toast.error('One or more image URLs are invalid');
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      category_id: formData.category_id,
      images: formData.images.filter((i) => i.trim()),
      stock: parseInt(formData.stock) || 0,
      featured: formData.featured,
      sizes: formData.sizes.filter((i) => i.trim()),
      colors: formData.colors.filter((i) => i.trim()),
      // sizes: formData.sizes,
      // colors: formData.colors,
    };

    try {
      if (editingProduct) {
        await productApi.update(editingProduct.id, payload);
        toast.success("Product updated");
      } else {
        await productApi.create(payload);
        toast.success("Product created");
      }

      // Instead of closing immediately, open confirmation dialog
      setConfirmDialogOpen(true);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }

  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this product?')) return;
    try {
      await productApi.delete(id);
      toast.success('Product deleted');
      loadData();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Products</h1>
            <p className="text-stone-400 mt-1">{products.length} total products</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-product-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Product
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-stone-900 border-stone-700 text-white"
            data-testid="product-search"
          />
        </div>

        {/* Products Table */}
        <div className="bg-stone-900 border border-stone-800 overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="border-b border-stone-800">
                <th className="text-left p-4 text-stone-400 font-small">Product</th>
                <th className="text-left p-4 text-stone-400 font-small">Category</th>
                <th className="text-left p-4 text-stone-400 font-small">Price</th>
                <th className="text-left p-4 text-stone-400 font-small">Stock</th>
                <th className="text-left p-4 text-stone-400 font-small">Status</th>
                <th className="text-right p-4 text-stone-400 font-small">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-stone-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-stone-500">
                    No products found
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => (
                  <tr
                    key={product.id}
                    className="border-b border-stone-800 hover:bg-stone-800/50"
                    data-testid={`product-row-${product.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-stone-800 rounded flex-shrink-0">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt=""
                              className="w-full h-full object-cover rounded"
                            />
                          ) : (
                            <Package className="w-6 h-6 m-3 text-stone-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-small text-white">{product.name}</p>
                          <p className="text-sm text-stone-500 truncate max-w-xs">
                            {product.description}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-stone-300">{product.category_name}</td>
                    <td className="p-4">
                      <p className="text-emerald-400 font-bold">
                        KES {product.price.toLocaleString()}
                      </p>
                      {product.original_price && (
                        <p className="text-sm text-stone-500 line-through">
                          KES {product.original_price.toLocaleString()}
                        </p>
                      )}
                    </td>
                    <td className="p-4 text-stone-300">{product.stock}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {product.featured && (
                          <Badge className="bg-purple-500/20 text-purple-400">Featured</Badge>
                        )}
                        {product.stock === 0 ? (
                          <Badge className="bg-red-500/20 text-red-400">Out of Stock</Badge>
                        ) : product.stock <= 5 ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Low Stock</Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400">In Stock</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(product)}
                          className="text-stone-400 hover:text-white"
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          className="text-stone-400 hover:text-red-400"
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-stone-900 border-stone-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </DialogTitle>
              <DialogDescription className="text-stone-400">
                Create or update product details below.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Product Name */}
                <div className="col-span-2">
                  <Label className="text-stone-300">Product Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    placeholder="e.g. Wireless Headphones"
                    data-testid="product-name-input"
                  />
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <Label className="text-stone-300">Description</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full mt-1 p-3 bg-stone-800 border border-stone-700 text-white rounded-none"
                    rows={3}
                    placeholder="Product description..."
                    data-testid="product-description-input"
                  />
                </div>
                {/* Sizes */}
                <div className="col-span-1">
                  <Label className="text-stone-300">Product Sizes (optional)</Label>

                  <Select
                    onValueChange={(val) => {
                      if (!formData.sizes.includes(val)) {
                        setFormData({
                          ...formData,
                          sizes: [...formData.sizes, val]
                        })
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-800 border-stone-700 text-white">
                      {sizes.map((size) => (
                        <SelectItem
                          key={size.id}
                          value={size.name}
                          className="text-white"
                        >
                          {size.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Selected sizes */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.sizes.map((size) => (
                      <Badge
                        key={size}
                        variant="secondary"
                        className="cursor-pointer select-none"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            sizes: formData.sizes.filter(s => s !== size)
                          })
                        }
                      >
                        {size} ✕
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Colors */}
                <div className="col-span-1">
                  <Label className="text-stone-300">Colors (optional)</Label>

                  <Select
                    onValueChange={(val) => {
                      if (!formData.colors.includes(val)) {
                        setFormData({
                          ...formData,
                          colors: [...formData.colors, val]
                        })
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 bg-stone-800 border-stone-700 text-white">
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>

                    <SelectContent className="bg-stone-800 border-stone-700 text-white">
                      {colors.map((color) => (
                        <SelectItem
                          key={color.id}
                          value={color.name}
                          className="text-white"
                        >
                          {color.name}
                        </SelectItem>
                      ))}
                    </SelectContent>

                  </Select>

                  {/* Selected colors */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.colors.map((color) => (
                      <Badge
                        key={color}
                        variant="secondary"
                        className="cursor-pointer select-none"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            colors: formData.colors.filter(c => c !== color)
                          })
                        }
                      >
                        {color} ✕
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <Label className="text-stone-300">Price (KES) *</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    placeholder="0"
                    data-testid="product-price-input"
                  />
                </div>

                {/* Original Price */}
                <div>
                  <Label className="text-stone-300">Original Price (Optional)</Label>
                  <Input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) =>
                      setFormData({ ...formData, original_price: e.target.value })
                    }
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    placeholder="For showing discount"
                  />
                </div>

                {/* Category */}
                <div>
                  <Label className="text-stone-300">Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(val) =>
                      setFormData({ ...formData, category_id: val })
                    }
                  >
                    <SelectTrigger
                      className="mt-1 bg-stone-800 border-stone-700 text-white"
                      data-testid="product-category-select"
                    >
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="bg-stone-800 border-stone-700">
                      {categories.map((cat) => (
                        <SelectItem
                          key={cat.id}
                          value={cat.id}
                          className="text-white"
                        >
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Stock */}
                <div>
                  <Label className="text-stone-300">Stock</Label>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) =>
                      setFormData({ ...formData, stock: e.target.value })
                    }
                    className="mt-1 bg-stone-800 border-stone-700 text-white"
                    placeholder="0"
                    data-testid="product-stock-input"
                  />
                </div>

                {/* Images with previews */}
                <div className="col-span-2">
                  <Label className="text-stone-300">Image URLs (max 8)</Label>
                  {formData.images.map((img, i) => (
                    <div key={i} className="flex items-center gap-3 mt-2">
                      <Input
                        value={img}
                        onChange={(e) => updateImage(i, e.target.value)}
                        className={`bg-stone-800 border-stone-700 text-white flex-1 ${img && !isValidUrl(img) ? "border-red-500" : ""
                          }`}
                        placeholder={`Image ${i + 1} URL`}
                      />
                      {img && isValidUrl(img) && (
                        <img
                          src={img}
                          alt={`Preview ${i + 1}`}
                          className="w-16 h-16 object-cover rounded border border-stone-700"
                        />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => removeImage(i)}
                        className="text-red-400"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  {formData.images.length < 8 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={addImage}
                      className="mt-2 text-stone-300 border-stone-700"
                    >
                      Add Another Image
                    </Button>
                  )}
                </div>

                {/* Featured Checkbox */}
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, featured: !!checked })
                    }
                    data-testid="product-featured-checkbox"
                  />
                  <Label
                    htmlFor="featured"
                    className="text-stone-300 cursor-pointer"
                  >
                    Featured Product
                  </Label>
                </div>
              </div>

              {/* Footer */}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-stone-700 text-stone-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-product-btn"
                >
                  {editingProduct ? "Update Product" : "Add Product"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Confirm Action After Successful Product Insert */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Product Saved</DialogTitle>
              <DialogDescription>
                Do you want to add another product?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={() => {
                  resetForm();           // YES → reset form
                  setConfirmDialogOpen(false);
                }}
              >
                Yes
              </Button>
              <Button
                onClick={() => {
                  setConfirmDialogOpen(false);
                  setDialogOpen(false);  // close product dialog
                  loadData();            // reload product list
                }}
                variant="secondary"
              >
                No
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
